import profileJson from '../data/nedProfileE1aAmi2027.json'
import type { HourlySlot } from './energyHistory'

/** Apply profile completion when either stream covers less than this share of the year. */
export const PROFILE_COVERAGE_THRESHOLD = 0.85
/** Ignore near-zero weights when estimating annual totals. */
const MIN_WEIGHT = 0.05
/**
 * Below this share of a stream's year, scaling up what was measured says more
 * about the season it was measured in than about the household. Measured on the
 * captured meters: six weeks ending in June or September carry 0.16–0.23 of the
 * year's feed-in and land within a fifth of the truth, while the same six weeks
 * ending in December carry 0.02 and land anywhere between nothing and the truth.
 */
export const MIN_CREDIBLE_WEIGHT = 0.15

export interface NedHourlyProfile {
  year: number
  category: string
  type: string
  hours: number
  a: number[]
  i: number[]
  monthlyA: number[]
  monthlyI: number[]
  sumA: number
  sumI: number
}

const profile = profileJson as NedHourlyProfile

/** Profile calendar year embedded in the NED fractions (holidays / DST as published). */
export const NED_PROFILE_YEAR = profile.year

export function getE1aAmiHourlyFractions(): NedHourlyProfile {
  return profile
}

/** Day-of-year 0..364 for a non-leap calendar date (month 1–12). */
function dayOfYearNonLeap(month: number, day: number): number {
  const ends = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334]
  return ends[month - 1]! + (day - 1)
}

/**
 * The clock the NEDU profile is written in: Dutch winter time (UTC+1) all year,
 * no daylight saving. Its feed-in peaks an hour after solar noon in UTC in every
 * month, with no jump at the clock changes, so reading its hours as UTC put every
 * profile-filled hour an hour late: the sun, the evening draw, against the prices.
 */
const PROFILE_UTC_OFFSET_MS = 3_600_000

/**
 * Map an instant onto the E1A hourly profile index (0..8759) by calendar
 * month/day/hour on the profile's own clock (UTC+1), ignoring the source year
 * (holidays stay as in the published profile year).
 */
export function profileHourIndex(date: Date): number {
  const onProfileClock = new Date(date.getTime() + PROFILE_UTC_OFFSET_MS)
  const month = onProfileClock.getUTCMonth() + 1
  const day = Math.min(onProfileClock.getUTCDate(), month === 2 ? 28 : 31)
  const hour = onProfileClock.getUTCHours()
  const doy = Math.min(364, Math.max(0, dayOfYearNonLeap(month, day)))
  return doy * 24 + hour
}

export interface ProfileWeights {
  weightA: number
  weightI: number
}

/** Sum profile fractions over the measured hourly slots. */
export function profileWeightForSlots(slots: HourlySlot[]): ProfileWeights {
  let weightA = 0
  let weightI = 0
  const seen = new Set<number>()
  for (const slot of slots) {
    const idx = profileHourIndex(new Date(slot.time))
    if (seen.has(idx)) continue
    seen.add(idx)
    weightA += profile.a[idx] ?? 0
    weightI += profile.i[idx] ?? 0
  }
  return { weightA, weightI }
}

export interface AnnualGridTotalsKwh {
  deliveredKwh: number
  returnedKwh: number
  weightA: number
  weightI: number
  /** False when the measured window is too small a slice of this stream's year. */
  deliveredCredible: boolean
  returnedCredible: boolean
}

/**
 * Scale measured delivered/returned to a full-year estimate using E1A AMI weights.
 */
export function estimateAnnualGridTotals(slots: HourlySlot[]): AnnualGridTotalsKwh {
  const { weightA, weightI } = profileWeightForSlots(slots)
  let deliveredWh = 0
  let returnedWh = 0
  for (const slot of slots) {
    deliveredWh += slot.delivered
    returnedWh += slot.returned
  }
  const deliveredKwh = deliveredWh / 1000 / Math.max(weightA, MIN_WEIGHT)
  const returnedKwh = returnedWh / 1000 / Math.max(weightI, MIN_WEIGHT)
  return {
    deliveredKwh,
    returnedKwh,
    weightA,
    weightI,
    deliveredCredible: weightA >= MIN_CREDIBLE_WEIGHT,
    returnedCredible: weightI >= MIN_CREDIBLE_WEIGHT,
  }
}

/** Month (0–11) × hour (0–23) average spot price from measured entries. */
export function buildMonthHourPriceBuckets(
  priceEntries: Iterable<[string, number]>
): Float64Array {
  const sum = new Float64Array(12 * 24)
  const count = new Float64Array(12 * 24)
  for (const [iso, price] of priceEntries) {
    const d = new Date(iso)
    const key = d.getUTCMonth() * 24 + d.getUTCHours()
    sum[key] += price
    count[key] += 1
  }
  const buckets = new Float64Array(12 * 24)
  let globalSum = 0
  let globalCount = 0
  for (let i = 0; i < sum.length; i++) {
    if (count[i] > 0) {
      buckets[i] = sum[i]! / count[i]!
      globalSum += sum[i]!
      globalCount += count[i]!
    }
  }
  const fallback = globalCount > 0 ? globalSum / globalCount : 0
  for (let i = 0; i < buckets.length; i++) {
    if (count[i] === 0) buckets[i] = fallback
  }
  return buckets
}

const HOURS_PER_YEAR = 8760
/** A day needs at least this share of an average day's profile weight to be judged. */
const MIN_DAY_WEIGHT = 1e-4
/** Fewer measured days than this says nothing about how lumpy a household is. */
const MIN_DAYS_FOR_SPREAD = 5

/** How one measured day compares with what the profile expected of it. */
interface DayFactor {
  delivered: number
  returned: number
}

/**
 * How much each measured day drew and fed back relative to what the national
 * profile expects of it. A household with panels that swing between nothing and a
 * full day's surplus has a wide spread; the profile itself, being an average over
 * many homes, has none.
 *
 * Both streams come from the same day so the pair stays together: a bright day
 * with a big surplus and the evening draw that followed it belong to each other,
 * and splitting them would invent days no household ever had.
 */
function measuredDayFactors(
  measuredSlots: HourlySlot[],
  annualDeliveredKwh: number,
  annualReturnedKwh: number
): DayFactor[] {
  interface DayTotals {
    actualA: number
    expectedA: number
    actualI: number
    expectedI: number
  }
  const perDay = new Map<string, DayTotals>()
  for (const slot of measuredSlots) {
    const day = slot.time.slice(0, 10)
    const entry = perDay.get(day) ?? { actualA: 0, expectedA: 0, actualI: 0, expectedI: 0 }
    const idx = profileHourIndex(new Date(slot.time))
    entry.actualA += slot.delivered / 1000
    entry.actualI += slot.returned / 1000
    entry.expectedA += annualDeliveredKwh * (profile.a[idx] ?? 0)
    entry.expectedI += annualReturnedKwh * (profile.i[idx] ?? 0)
    perDay.set(day, entry)
  }
  const factors: DayFactor[] = []
  for (const day of perDay.values()) {
    // A day the profile expects nothing of says nothing about the spread, so it
    // keeps the profile's own level rather than dividing by near-zero.
    factors.push({
      delivered:
        day.expectedA > annualDeliveredKwh * MIN_DAY_WEIGHT ? day.actualA / day.expectedA : 1,
      returned:
        day.expectedI > annualReturnedKwh * MIN_DAY_WEIGHT ? day.actualI / day.expectedI : 1,
    })
  }
  return factors.length >= MIN_DAYS_FOR_SPREAD ? factors : [{ delivered: 1, returned: 1 }]
}

/** Every hour of the year ending at `endMs`, oldest first. */
function yearEndingAt(endMs: number): string[] {
  const hours: string[] = new Array(HOURS_PER_YEAR)
  for (let i = 0; i < HOURS_PER_YEAR; i++) {
    hours[i] = new Date(endMs - (HOURS_PER_YEAR - 1 - i) * 3_600_000).toISOString()
  }
  return hours
}

export interface CompletedYear {
  slots: HourlySlot[]
  priceEntries: Array<[string, number]> | null
}

/**
 * Complete a partial year on the measured calendar: every hour the meter reported
 * is kept exactly as measured, and only the hours we never saw are painted from
 * the profile, scaled so the whole year adds up to the estimated annual totals.
 *
 * Painted days carry the day-to-day spread the household itself showed. Without
 * that, the filled months are smooth average days, every one of which a small
 * battery can absorb entirely — which flatters small batteries and overstates how
 * much of a real, lumpy surplus they would capture.
 */
export function completeYearFromMeasured(
  measuredSlots: HourlySlot[],
  annualDeliveredKwh: number,
  annualReturnedKwh: number,
  spotPriceEntries?: Array<[string, number]> | null
): CompletedYear {
  const measuredByHour = new Map(measuredSlots.map((slot) => [slot.time, slot]))
  const lastMs = measuredSlots.reduce(
    (latest, slot) => Math.max(latest, new Date(slot.time).getTime()),
    0
  )
  const dayFactors = measuredDayFactors(measuredSlots, annualDeliveredKwh, annualReturnedKwh)

  const monthHour =
    spotPriceEntries && spotPriceEntries.length > 0
      ? buildMonthHourPriceBuckets(spotPriceEntries)
      : null
  const spotPrices = new Map(spotPriceEntries ?? [])

  const slots: HourlySlot[] = []
  const priceEntries: Array<[string, number]> = []
  const painted: Array<{ index: number; delivered: number; returned: number; day: number }> = []
  let paintedDay = -1
  let lastPaintedDay = ''

  for (const time of yearEndingAt(lastMs)) {
    const day = time.slice(0, 10)
    const measured = measuredByHour.get(time)
    if (measured) {
      slots.push(measured)
    } else {
      // Every calendar day with painted hours takes the next day factor, a gap
      // inside a measured day included, so the year may also start on a measured
      // hour; reshapePaintedDays gives the day the pattern of that same factor.
      if (day !== lastPaintedDay) {
        paintedDay += 1
        lastPaintedDay = day
      }
      const idx = profileHourIndex(new Date(time))
      const factor = dayFactors[paintedDay % dayFactors.length]!
      const slot: HourlySlot = {
        time,
        delivered: annualDeliveredKwh * (profile.a[idx] ?? 0) * 1000 * factor.delivered,
        returned: annualReturnedKwh * (profile.i[idx] ?? 0) * 1000 * factor.returned,
        produced: 0,
      }
      painted.push({
        index: slots.length,
        delivered: slot.delivered,
        returned: slot.returned,
        day: paintedDay,
      })
      slots.push(slot)
    }
    if (monthHour) {
      // Every hour at its real EPEX price, painted or measured: the report fetches
      // a full year for this. The month/hour average only covers gaps in the feed.
      const spotPrice = spotPrices.get(time)
      priceEntries.push([time, spotPrice ?? monthHour[monthHourKey(time)] ?? 0])
    }
  }

  // The day spread moves energy around, so rescale what was painted to hit the
  // annual estimate exactly: measured hours must stay untouched.
  rescalePainted(slots, painted, measuredSlots, annualDeliveredKwh, annualReturnedKwh)
  reshapePaintedDays(slots, painted, measuredSlots)

  return { slots, priceEntries: monthHour ? priceEntries : (spotPriceEntries ?? null) }
}

interface DayPattern {
  /** Wh per UTC hour (0–23) of one measured day. */
  delivered: Float64Array
  returned: Float64Array
}

/**
 * The hour-by-hour pattern of every measured UTC day, in the order
 * `measuredDayFactors` builds its factors. A partial day (the window's edges)
 * lends the pattern of the nearest whole day.
 */
function measuredDayPatterns(measuredSlots: HourlySlot[]): DayPattern[] {
  const perDay = new Map<string, HourlySlot[]>()
  for (const slot of measuredSlots) {
    const day = slot.time.slice(0, 10)
    const hours = perDay.get(day)
    if (hours) hours.push(slot)
    else perDay.set(day, [slot])
  }
  const raw: Array<DayPattern | null> = []
  for (const hours of perDay.values()) {
    if (hours.length !== 24) {
      raw.push(null)
      continue
    }
    const pattern: DayPattern = { delivered: new Float64Array(24), returned: new Float64Array(24) }
    for (const slot of hours) {
      const hour = new Date(slot.time).getUTCHours()
      pattern.delivered[hour] = slot.delivered
      pattern.returned[hour] = slot.returned
    }
    raw.push(pattern)
  }
  const whole = raw.flatMap((pattern, k) => (pattern ? [k] : []))
  if (whole.length === 0) return []
  const nearestWhole = (k: number) =>
    whole.reduce((best, w) => (Math.abs(w - k) < Math.abs(best - k) ? w : best), whole[0]!)
  // Too few days to cycle through: the factors fell back to one flat (1, 1) as well.
  if (raw.length < MIN_DAYS_FOR_SPREAD) return whole.map((w) => raw[w]!)
  return raw.map((pattern, k) => pattern ?? raw[nearestWhole(k)]!)
}

/**
 * Give every painted day the hour-by-hour pattern of a measured day of this
 * household, keeping the day's totals. The profile is a sum over many homes, so
 * every daylight hour of it carries import and feed-in at once — some homes draw
 * more than their panels give while others export — and the hourly netting then
 * cancels a fifth of the year's import against feed-in. One household rarely does
 * both within an hour: its own days keep its evening, its cloudy hours and its own
 * small overlap. Each painted day takes the pattern of the measured day whose
 * factor it already carries (`day`, its place in the factor cycle).
 */
function reshapePaintedDays(
  slots: HourlySlot[],
  painted: Array<{ index: number; day: number }>,
  measuredSlots: HourlySlot[]
): void {
  const patterns = measuredDayPatterns(measuredSlots)
  if (patterns.length === 0 || painted.length === 0) return
  const byDay = new Map<number, number[]>()
  for (const { index, day } of painted) {
    const indices = byDay.get(day)
    if (indices) indices.push(index)
    else byDay.set(day, [index])
  }
  for (const [day, indices] of byDay) {
    const pattern = patterns[day % patterns.length]!
    const hours = indices.map((k) => new Date(slots[k]!.time).getUTCHours())
    for (const stream of ['delivered', 'returned'] as const) {
      const weights = hours.map((hour) => pattern[stream][hour]!)
      let totalWeight = 0
      for (const weight of weights) totalWeight += weight
      // A stream the pattern day lacks keeps the profile's shape.
      if (totalWeight <= 0) continue
      let total = 0
      for (const k of indices) total += slots[k]![stream]
      indices.forEach((k, n) => {
        slots[k]![stream] = (total * weights[n]!) / totalWeight
      })
    }
  }
}

function monthHourKey(iso: string): number {
  const date = new Date(iso)
  return date.getUTCMonth() * 24 + date.getUTCHours()
}

function rescalePainted(
  slots: HourlySlot[],
  painted: Array<{ index: number; delivered: number; returned: number }>,
  measuredSlots: HourlySlot[],
  annualDeliveredKwh: number,
  annualReturnedKwh: number
): void {
  if (painted.length === 0) return
  const measuredKwh = measuredSlots.reduce(
    (totals, slot) => ({
      delivered: totals.delivered + slot.delivered / 1000,
      returned: totals.returned + slot.returned / 1000,
    }),
    { delivered: 0, returned: 0 }
  )
  const paintedKwh = painted.reduce(
    (totals, p) => ({
      delivered: totals.delivered + p.delivered / 1000,
      returned: totals.returned + p.returned / 1000,
    }),
    { delivered: 0, returned: 0 }
  )
  const factor = (target: number, actual: number) =>
    actual > 0 ? Math.max(0, target) / actual : 0
  const scaleA = factor(annualDeliveredKwh - measuredKwh.delivered, paintedKwh.delivered)
  const scaleI = factor(annualReturnedKwh - measuredKwh.returned, paintedKwh.returned)
  for (const p of painted) {
    const slot = slots[p.index]!
    slot.delivered = p.delivered * scaleA
    slot.returned = p.returned * scaleI
  }
}

export interface PreparedAnnualSeries {
  /** Slots used for economics (measured or synthetic year). */
  slots: HourlySlot[]
  periodDays: number
  /** Spot map for the economics series; null when fixed tariffs / no prices. */
  priceEntries: Array<[string, number]> | null
  /** True when the year was completed with the E1A AMI profile. */
  usedProfileCompletion: boolean
  weightA: number
  weightI: number
  annualDeliveredKwh: number
  annualReturnedKwh: number
  /**
   * False when the measured window is too thin a slice of that stream's year for
   * the annual estimate to mean anything — a winter-only history says nothing
   * about a solar surplus, and the economics built on it should not be presented
   * as a number the household can count on.
   */
  deliveredEstimateCredible: boolean
  returnedEstimateCredible: boolean
}

/**
 * Whether the feed-in half of the advice rests on an assumption rather than on
 * anything the meter saw: a year that had to be completed, from a window holding
 * too little of this household's solar year — and a household that does feed back,
 * because without a surplus the case for a battery never rested on one.
 */
export function solarEstimateIsUncertain(series: PreparedAnnualSeries): boolean {
  return (
    series.usedProfileCompletion &&
    !series.returnedEstimateCredible &&
    series.annualReturnedKwh > 0
  )
}

export interface PrepareAnnualSlotsParams {
  measuredSlots: HourlySlot[]
  measuredPeriodDays: number
  /**
   * Spot prices (ISO hour → EUR/kWh). Pass the full year ending at the last
   * measured hour, not just the measured window, so a completed year prices its
   * painted hours at their real EPEX hour as well.
   */
  priceEntries?: Array<[string, number]> | null
}

/**
 * Choose measured data (full coverage) or a profile-synthesized year for battery economics.
 * Inverter sizing should keep using measured peaks separately.
 */
export function prepareAnnualSlots(params: PrepareAnnualSlotsParams): PreparedAnnualSeries {
  const { measuredSlots, measuredPeriodDays, priceEntries } = params
  const totals = estimateAnnualGridTotals(measuredSlots)
  const { weightA, weightI, deliveredKwh, returnedKwh } = totals

  const coverageOk =
    weightA >= PROFILE_COVERAGE_THRESHOLD && weightI >= PROFILE_COVERAGE_THRESHOLD

  if (coverageOk || measuredSlots.length === 0) {
    return {
      slots: measuredSlots,
      periodDays: measuredPeriodDays,
      priceEntries: priceEntries ?? null,
      usedProfileCompletion: false,
      weightA,
      weightI,
      annualDeliveredKwh: deliveredKwh,
      annualReturnedKwh: returnedKwh,
      deliveredEstimateCredible: totals.deliveredCredible,
      returnedEstimateCredible: totals.returnedCredible,
    }
  }

  const completed = completeYearFromMeasured(
    measuredSlots,
    deliveredKwh,
    returnedKwh,
    priceEntries
  )

  return {
    slots: completed.slots,
    periodDays: 365,
    priceEntries: completed.priceEntries,
    usedProfileCompletion: true,
    weightA,
    weightI,
    annualDeliveredKwh: deliveredKwh,
    annualReturnedKwh: returnedKwh,
    deliveredEstimateCredible: totals.deliveredCredible,
    returnedEstimateCredible: totals.returnedCredible,
  }
}
